import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Send, User, Clock, CheckCircle2, Flame, CalendarDays, RotateCcw,
  Pencil, Trash2, Building2,
} from 'lucide-react';
import { getFireById, updateFire, addNoteToFire, updateNoteInFire, deleteNoteFromFire } from '../services/firebase/fires';
import { getInternalUsers } from '../services/userService';
import BackButton from '../components/BackButton';
import { useAuth } from '../contexts/AuthContext';
import { useResponsive } from '../contexts/ResponsiveContext';
import { useCompanyId } from '../hooks/useCompanyId';
import { formatTimeAgo } from '../utils/timeAgo';
import PortalBackground from '../components/PortalBackground';

// Note: 'Pending' is stored in the data but shown to clients as "In Progress"
const STATUS_META = {
  Active: {
    label: 'Active',
    icon: Flame,
    badge: 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300',
  },
  Pending: {
    label: 'In Progress',
    icon: Clock,
    badge: 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
  },
  Resolved: {
    label: 'Resolved',
    icon: CheckCircle2,
    badge: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300',
  },
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.Active;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${meta.badge}`}>
      <Icon className="w-3.5 h-3.5" />
      {meta.label}
    </span>
  );
};

const PortalFireOverview = () => {
  const { fireId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { companyId, loading: companyIdLoading } = useCompanyId();
  const { isMobile } = useResponsive();

  const [fire, setFire] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [sendingNote, setSendingNote] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [editedNoteText, setEditedNoteText] = useState('');
  const [internalUsers, setInternalUsers] = useState([]);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const notesEndRef = useRef(null);

  useEffect(() => {
    const loadData = async () => {
      if (!currentUser || companyIdLoading) return;

      try {
        setLoading(true);
        const [fireData, usersData] = await Promise.all([
          getFireById(fireId),
          getInternalUsers()
        ]);

        if (!fireData) {
          setFire(null);
          setLoading(false);
          return;
        }

        // Same scope as MyFiresPage: company-wide accountId or legacy per-user uid
        const effectiveAccountId = companyId || currentUser.uid;
        const canView =
          fireData.accountId === currentUser.uid ||
          fireData.accountId === effectiveAccountId;

        if (!canView) {
          navigate('/portal/fires', { replace: true });
          setLoading(false);
          return;
        }

        setFire({
          ...fireData,
          notes: Array.isArray(fireData.notes) ? fireData.notes : []
        });
        setInternalUsers(usersData);
        setLoading(false);
      } catch (error) {
        console.error('Error loading data:', error);
        setLoading(false);
      }
    };

    loadData();
  }, [fireId, currentUser, navigate, companyId, companyIdLoading]);

  // Keep the conversation scrolled to the latest note
  useEffect(() => {
    notesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [fire?.notes?.length]);

  const addNote = async () => {
    if (!newNote.trim() || sendingNote) return;
    try {
      setSendingNote(true);
      const newNoteData = await addNoteToFire(fire.id, newNote);
      setFire(prev => ({
        ...prev,
        notes: Array.isArray(prev.notes) ? [...prev.notes, newNoteData] : [newNoteData]
      }));
      setNewNote('');
    } catch (error) {
      console.error('Error adding note:', error);
      alert('Failed to add note. Please try again.');
    } finally {
      setSendingNote(false);
    }
  };

  // Enter sends, Shift+Enter adds a newline
  const handleComposerKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      addNote();
    }
  };

  const editNote = (index) => {
    setEditingNote(index);
    setEditedNoteText(fire.notes[index].noteText);
  };

  const saveNote = async (index) => {
    try {
      const updatedNote = await updateNoteInFire(fire.id, fire.notes[index].id, editedNoteText);
      setFire(prev => ({
        ...prev,
        notes: prev.notes.map((note, i) => i === index ? updatedNote : note)
      }));
      setEditingNote(null);
      setEditedNoteText('');
    } catch (error) {
      console.error('Error updating note:', error);
    }
  };

  const cancelNoteEdit = () => {
    setEditingNote(null);
    setEditedNoteText('');
  };

  const deleteNote = async (index) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      const noteToDelete = fire.notes[index];
      await deleteNoteFromFire(fire.id, noteToDelete.id);
      setFire(prev => ({
        ...prev,
        notes: prev.notes.filter((_, i) => i !== index)
      }));
    } catch (error) {
      console.error('Error deleting note:', error);
      alert('Failed to delete note. Please try again.');
    }
  };

  // Handle status update — persisted to Firebase
  const handleStatusUpdate = async (newStatus) => {
    if (!fire || newStatus === fire.status || updatingStatus) return;

    try {
      setUpdatingStatus(true);
      await updateFire(fire.id, { status: newStatus });
      setFire(prev => ({ ...prev, status: newStatus }));
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status. Please try again.');
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (loading) {
    return (
      <div className="relative container mx-auto px-4 py-6 min-h-screen">
        <PortalBackground />
        <div className="relative grid grid-cols-1 lg:grid-cols-3 gap-6 mt-16">
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 animate-pulse space-y-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-3 rounded bg-gray-100 dark:bg-gray-800" style={{ width: `${80 - i * 12}%` }}></div>
            ))}
          </div>
          <div className="lg:col-span-2 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 animate-pulse space-y-4">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="h-3 rounded bg-gray-100 dark:bg-gray-800" style={{ width: `${90 - i * 10}%` }}></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!fire) {
    return (
      <div className="relative container mx-auto px-4 py-6 min-h-screen">
        <PortalBackground />
        <div className="relative rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm flex flex-col items-center justify-center py-16 px-6 text-center mt-10">
          <Flame className="w-10 h-10 text-gray-300 dark:text-gray-600 mb-3" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Fire not found</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">It may have been removed, or the link is incorrect.</p>
          <button
            onClick={() => navigate('/portal/fires')}
            className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold transition-colors"
          >
            Back to My Fires
          </button>
        </div>
      </div>
    );
  }

  const notes = fire.notes || [];

  return (
    <div className={`relative container mx-auto ${isMobile ? 'px-3 py-4' : 'px-4 py-6'} min-h-screen`}>
      <PortalBackground />
      <div className="relative">
      {/* Header */}
      <div className={`relative ${isMobile ? 'mb-4' : 'mb-6'}`}>
        <div className="absolute left-0 top-1/2 -translate-y-1/2 z-10">
          <BackButton onClick={() => navigate('/portal/fires')} />
        </div>
        <div className={`text-center ${isMobile ? 'px-14' : 'px-32'} py-1`}>
          <h1 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold tracking-tight text-gray-900 dark:text-white truncate`}>{fire.name}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Reported {formatTimeAgo(fire.dateReported)}
          </p>
        </div>
        <div className="absolute right-0 top-1/2 -translate-y-1/2 z-10">
          <StatusBadge status={fire.status} />
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Fire info */}
        <div className="lg:col-span-1">
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-5 lg:sticky lg:top-6">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center justify-center gap-2 mb-4 text-center">
              <Flame className="w-4 h-4 text-red-600" />
              Fire Info
            </h2>

            <div className="space-y-4">
              {/* Status */}
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 flex items-center justify-center shrink-0">
                  <Clock className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Status</p>
                  <div className="mt-0.5"><StatusBadge status={fire.status} /></div>
                </div>
              </div>

              {/* Account */}
              {fire.companyName && (
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 flex items-center justify-center shrink-0">
                    <Building2 className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Account</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{fire.companyName}</p>
                  </div>
                </div>
              )}

              {/* Assigned To */}
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 flex items-center justify-center shrink-0">
                  <User className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Assigned To</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {(fire.assignedTo || []).join(', ') || 'Unassigned'}
                  </p>
                </div>
              </div>

              {/* Date Reported */}
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 flex items-center justify-center shrink-0">
                  <CalendarDays className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Reported</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {new Date(fire.dateReported).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>

              {/* Status actions */}
              <div className="pt-2 border-t border-gray-100 dark:border-gray-800 space-y-2">
                {fire.status === 'Active' && (
                  <>
                    <button
                      onClick={() => handleStatusUpdate('Pending')}
                      disabled={updatingStatus}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white bg-amber-500 hover:bg-amber-600 disabled:opacity-60 transition-colors"
                    >
                      <Clock className="w-4 h-4" />
                      {updatingStatus ? 'Updating...' : 'Mark In Progress'}
                    </button>
                    <button
                      onClick={() => handleStatusUpdate('Resolved')}
                      disabled={updatingStatus}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 transition-colors"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      {updatingStatus ? 'Updating...' : 'Mark as Resolved'}
                    </button>
                  </>
                )}
                {fire.status === 'Pending' && (
                  <button
                    onClick={() => handleStatusUpdate('Resolved')}
                    disabled={updatingStatus}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 transition-colors"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    {updatingStatus ? 'Updating...' : 'Mark as Resolved'}
                  </button>
                )}
                {fire.status === 'Resolved' && (
                  <button
                    onClick={() => handleStatusUpdate('Active')}
                    disabled={updatingStatus}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-60 transition-colors"
                  >
                    <RotateCcw className="w-4 h-4" />
                    {updatingStatus ? 'Reopening...' : 'Reopen Fire'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Updates / conversation */}
        <div className="lg:col-span-2">
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm flex flex-col">
            {/* Description */}
            <div className="p-5 border-b border-gray-100 dark:border-gray-800">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1.5 text-center">Description</p>
              <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap leading-relaxed">{fire.description}</p>
            </div>

            {/* Notes / updates list */}
            <div className="p-5 flex-1 overflow-y-auto" style={{ maxHeight: '28rem' }}>
              {notes.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-center">
                  <Send className="w-8 h-8 text-gray-300 dark:text-gray-600 mb-2" />
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No updates yet</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    Progress notes from our team will show up here.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {notes.map((note, index) => {
                    const authorName = typeof note?.author === 'string'
                      ? note.author
                      : (note?.author?.name || note?.author?.displayName || note?.author?.email || 'User');
                    const noteText = typeof note?.noteText === 'string'
                      ? note.noteText
                      : (typeof note === 'string' ? note : '');
                    const authorProfileImage = note?.authorProfileImage
                      || internalUsers.find(u => u.id === note?.authorId)?.profileImage
                      || null;
                    const isMine = note?.authorId === currentUser?.uid;
                    return (
                      <div key={index} className="flex gap-2.5 group">
                        <div className="flex-shrink-0">
                          <div className="relative">
                            {authorProfileImage ? (
                              <img
                                src={authorProfileImage}
                                alt={authorName || 'User'}
                                className="w-8 h-8 rounded-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div className={`w-8 h-8 rounded-full bg-red-500 flex items-center justify-center text-white text-sm font-medium ${authorProfileImage ? 'hidden' : 'flex'}`}>
                              {(authorName || 'U').charAt(0)}
                            </div>
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2 mb-1">
                            <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">
                              {authorName || 'User'}
                            </span>
                            <span className="text-[11px] text-gray-400 dark:text-gray-500">
                              {formatTimeAgo(note.createdAt)}
                            </span>
                            {isMine && editingNote !== index && (
                              <span className="ml-auto flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  onClick={() => editNote(index)}
                                  className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                  title="Edit note"
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => deleteNote(index)}
                                  className="p-1 rounded text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                  title="Delete note"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </span>
                            )}
                          </div>
                          {editingNote === index ? (
                            <div className="space-y-2">
                              <textarea
                                value={editedNoteText}
                                onChange={(e) => setEditedNoteText(e.target.value)}
                                className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                                rows="3"
                                autoFocus
                              />
                              <div className="flex gap-2">
                                <button
                                  onClick={() => saveNote(index)}
                                  className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg transition-colors"
                                >
                                  Save
                                </button>
                                <button
                                  onClick={cancelNoteEdit}
                                  className="px-3 py-1.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 text-xs font-semibold rounded-lg transition-colors"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="inline-block px-3.5 py-2.5 text-sm leading-relaxed bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-2xl rounded-tl-sm">
                              <p className="whitespace-pre-wrap">{noteText}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <div ref={notesEndRef} />
                </div>
              )}
            </div>

            {/* Composer */}
            <div className="p-4 border-t border-gray-100 dark:border-gray-800">
              <form onSubmit={(e) => { e.preventDefault(); addNote(); }} className="flex items-end gap-2">
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  onKeyDown={handleComposerKeyDown}
                  placeholder="Add a note or update... (Enter to send)"
                  className="flex-1 px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                  rows="2"
                />
                <button
                  type="submit"
                  disabled={!newNote.trim() || sendingNote}
                  className="p-3 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 dark:disabled:bg-gray-700 text-white rounded-xl transition-colors shrink-0"
                  title="Send note"
                >
                  {sendingNote ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    <Send className="h-5 w-5" />
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default PortalFireOverview;
